import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    Chip,
    CircularProgress,
    Skeleton,
    Tabs,
    Tab,
    Divider,
    Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
    getPatientExercises,
    getPatientAiAnalyses,
    getPatientExerciseProgress,
} from "../../../api/services/api";

export function PatientVoiceExercises() {
    const { id: patientProfileId } = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);

    const { data: exercisesData, isLoading: exercisesLoading } = useQuery(
        ["patientExercises", patientProfileId],
        getPatientExercises,
        { enabled: !!patientProfileId }
    );

    const { data: analysesData, isLoading: analysesLoading } = useQuery(
        ["patientAiAnalyses", patientProfileId],
        getPatientAiAnalyses,
        { enabled: !!patientProfileId }
    );

    const { data: progressData } = useQuery(
        ["patientExerciseProgress", patientProfileId],
        getPatientExerciseProgress,
        { enabled: !!patientProfileId }
    );

    const exercises = exercisesData?.data || [];
    const analyses = analysesData?.data || [];
    const progress = progressData?.data || {};

    return (
        <Box sx={{ p: 3 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ mb: 2, textTransform: "none", color: "#64748b" }}
            >
                Back
            </Button>

            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Voice Exercises & AI Analysis
            </Typography>

            {/* AI Disclaimer */}
            <Alert
                severity="warning"
                icon={<ErrorOutlineIcon />}
                sx={{ mb: 3, borderRadius: 2 }}
            >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Important: AI analysis outputs are not conclusive diagnoses.
                </Typography>
                <Typography variant="caption">
                    The AI assistant provides suggestions to support your clinical judgment.
                    These outputs should not be solely relied upon for diagnosis or treatment decisions.
                </Typography>
            </Alert>

            {/* Progress Summary */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 2,
                    mb: 3,
                }}
            >
                {[
                    {
                        label: "Completed",
                        value: progress.totalCompleted || 0,
                        icon: <CheckCircleOutlineIcon sx={{ color: "#16a34a" }} />,
                    },
                    {
                        label: "Current Streak",
                        value: `${progress.currentStreak || 0} days`,
                        icon: <TrendingUpIcon sx={{ color: "#f59e0b" }} />,
                    },
                    {
                        label: "Completion Rate",
                        value: `${Math.round(progress.completionRate || 0)}%`,
                        icon: <AssignmentOutlinedIcon sx={{ color: "#6366f1" }} />,
                    },
                    {
                        label: "Pending Review",
                        value: analyses.filter((a: any) => !a.clinician_review).length,
                        icon: <RateReviewOutlinedIcon sx={{ color: "#dc2626" }} />,
                    },
                ].map((stat, i) => (
                    <Card
                        key={i}
                        sx={{
                            p: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            borderRadius: 2,
                        }}
                    >
                        {stat.icon}
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {stat.value}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                                {stat.label}
                            </Typography>
                        </Box>
                    </Card>
                ))}
            </Box>

            {/* Tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 3, borderBottom: "1px solid #e2e8f0" }}
            >
                <Tab
                    icon={<MicOutlinedIcon />}
                    iconPosition="start"
                    label={`Exercises (${exercises.length})`}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                />
                <Tab
                    icon={<SmartToyOutlinedIcon />}
                    iconPosition="start"
                    label={`AI Analyses (${analyses.length})`}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                />
            </Tabs>

            {/* Exercises Tab */}
            {tab === 0 && (
                <Box>
                    {exercisesLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {[1, 2, 3].map((i) => (
                                <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
                            ))}
                        </Box>
                    ) : exercises.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
                            <MicOutlinedIcon sx={{ fontSize: 48, mb: 1 }} />
                            <Typography>No exercises found for this patient</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {exercises.map((ex: any) => {
                                const isCompleted = ex.status === "COMPLETED";
                                const isMissed = ex.status === "MISSED";
                                return (
                                    <Card
                                        key={ex.id}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            borderLeft: `4px solid ${isCompleted
                                                    ? "#16a34a"
                                                    : isMissed
                                                        ? "#dc2626"
                                                        : "#94a3b8"
                                                }`,
                                            cursor: isCompleted ? "pointer" : "default",
                                            "&:hover": isCompleted
                                                ? { bgcolor: "#f8fafc" }
                                                : {},
                                        }}
                                        onClick={() =>
                                            isCompleted &&
                                            navigate(
                                                `/patients/patient-profile/${patientProfileId}/ai-review/${ex.ai_analysis?.id}`
                                            )
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box>
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {ex.template?.name || "Exercise"}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "#64748b" }}>
                                                    {ex.scheduled_date &&
                                                        new Date(ex.scheduled_date).toLocaleDateString(
                                                            "en-US",
                                                            { weekday: "short", month: "short", day: "numeric" }
                                                        )}
                                                    {" · "}
                                                    {ex.recordings?.length || 0} recordings
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <Chip
                                                    label={ex.status}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: "0.7rem",
                                                        bgcolor: isCompleted
                                                            ? "#dcfce7"
                                                            : isMissed
                                                                ? "#fee2e2"
                                                                : "#f1f5f9",
                                                        color: isCompleted
                                                            ? "#166534"
                                                            : isMissed
                                                                ? "#991b1b"
                                                                : "#64748b",
                                                    }}
                                                />
                                                {ex.ai_analysis && (
                                                    <Chip
                                                        icon={<SmartToyOutlinedIcon sx={{ fontSize: 14 }} />}
                                                        label={
                                                            ex.ai_analysis.clinician_review
                                                                ? "Reviewed"
                                                                : "Pending Review"
                                                        }
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "0.7rem",
                                                            bgcolor: ex.ai_analysis.clinician_review
                                                                ? "#f0fdf4"
                                                                : "#fef3c7",
                                                            color: ex.ai_analysis.clinician_review
                                                                ? "#166534"
                                                                : "#92400e",
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}

            {/* AI Analyses Tab */}
            {tab === 1 && (
                <Box>
                    {analysesLoading ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {[1, 2, 3].map((i) => (
                                <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                            ))}
                        </Box>
                    ) : analyses.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
                            <SmartToyOutlinedIcon sx={{ fontSize: 48, mb: 1 }} />
                            <Typography>No AI analyses available yet</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {analyses.map((analysis: any) => {
                                const hasReview = !!analysis.clinician_review;
                                const score = analysis.confidence_score;
                                return (
                                    <Card
                                        key={analysis.id}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            border: hasReview
                                                ? "1px solid #bbf7d0"
                                                : "1px solid #fde68a",
                                            "&:hover": { bgcolor: "#f8fafc" },
                                        }}
                                        onClick={() =>
                                            navigate(
                                                `/patients/patient-profile/${patientProfileId}/ai-review/${analysis.id}`
                                            )
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <Box sx={{ flex: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 1,
                                                        alignItems: "center",
                                                        mb: 1,
                                                    }}
                                                >
                                                    <SmartToyOutlinedIcon
                                                        sx={{ color: "#6366f1", fontSize: 20 }}
                                                    />
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {analysis.model_name || "AI Analysis"}
                                                    </Typography>
                                                </Box>

                                                {analysis.summary && (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "#64748b",
                                                            mb: 1,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                        }}
                                                    >
                                                        {analysis.summary}
                                                    </Typography>
                                                )}

                                                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                                    {analysis.created_at &&
                                                        new Date(analysis.created_at).toLocaleDateString(
                                                            "en-US",
                                                            {
                                                                month: "short",
                                                                day: "numeric",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            }
                                                        )}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                                                {score != null && (
                                                    <Chip
                                                        label={`${Math.round(score * 100)}%`}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor:
                                                                score >= 0.8
                                                                    ? "#dcfce7"
                                                                    : score >= 0.6
                                                                        ? "#fef3c7"
                                                                        : "#fee2e2",
                                                            color:
                                                                score >= 0.8
                                                                    ? "#166534"
                                                                    : score >= 0.6
                                                                        ? "#92400e"
                                                                        : "#991b1b",
                                                        }}
                                                    />
                                                )}
                                                <Chip
                                                    label={hasReview ? "Reviewed" : "Review"}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 600,
                                                        bgcolor: hasReview ? "#f0fdf4" : "#fef3c7",
                                                        color: hasReview ? "#166534" : "#92400e",
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default PatientVoiceExercises;
